import { TimezoneOption } from "@core/features/timezones";
import { DownloadICSButton } from "@themes/pollinator/ui/DownloadICSButton";
import { Meeting } from "@platform/types/platform.types";
import type { SelectableOption } from "@core/features/actions";
import { ProgramCalendarEventItem } from "../ProgramCalendarEventItem";
import { CalendarEventTimeItem } from "../CalendarEventTimeItem";

export type Props = {
  /** Meetings to display (takes precedence over selectableOptions) */
  meetings?: Meeting[];
  /** Scheduled time options from display_options (used when no meetings) */
  selectableOptions?: SelectableOption[];
  /** Title for calendar events when using selectableOptions */
  eventTitle?: string;
  /** Description for calendar events when using selectableOptions */
  eventDescription?: string;
  /** User's timezone for display */
  userTimezone?: TimezoneOption;
  /** Callback when calendar event is added */
  onAddCalendarEvent: (
    eventId: number | string | "all",
    calendarType: "google" | "ical"
  ) => void;
};

export function ProgramCalendarEventList({
  meetings,
  selectableOptions,
  eventTitle = "Peer Circle Meeting",
  eventDescription,
  userTimezone,
  onAddCalendarEvent,
}: Props) {
  const hasMeetings = meetings && meetings.length > 0;
  const hasOptions = selectableOptions && selectableOptions.length > 0;

  // Return null if no data to display
  if (!hasMeetings && !hasOptions) return null;

  return (
    <div className="flex flex-col gap-6">
      {/* Meetings take precedence */}
      {hasMeetings ? (
        <>
          {meetings.length > 1 && (
            <div className="flex justify-center self-end">
              <DownloadICSButton
                meetings={meetings}
                eventTitle="Meeting"
                onAddCalendarEvent={onAddCalendarEvent}
              />
            </div>
          )}

          <div className="flex flex-col gap-4">
            {meetings.map((meeting) => (
              <ProgramCalendarEventItem
                key={meeting.id}
                meeting={meeting}
                userTimezone={userTimezone}
                onAddCalendarEvent={onAddCalendarEvent}
              />
            ))}
          </div>
        </>
      ) : hasOptions ? (
        <div className="flex flex-col gap-6">
          {selectableOptions.map((option) => (
            <CalendarEventTimeItem
              key={option.id || `${option.date}-${option.startHour}`}
              option={option}
              eventTitle={eventTitle}
              eventDescription={eventDescription}
              userTimezone={userTimezone}
              onAddCalendarEvent={onAddCalendarEvent}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default ProgramCalendarEventList;

